import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ArrowRight, Check } from "lucide-react";
import { Reveal, Label } from "@/components/site/Motion";
import { useSettings } from "@/hooks/useSettings";
import { Input } from "@/components/ui/input";

const PACKAGES = [
  {
    key: "construction",
    name: "Construction",
    rateKey: "rate_classic",
    blurb: "Complete civil construction — foundation to finish.",
    includes: ["Structural RCC & brickwork", "Branded cement & TMT steel", "Plastering & waterproofing", "Standard flooring & fittings", "Structural warranty"],
  },
  {
    key: "combo",
    name: "Construction + Interior",
    rateKey: "rate_premium",
    blurb: "Turnkey — we build and fully finish your interiors.",
    includes: ["Everything in Construction", "Modular kitchen & wardrobes", "False ceiling & lighting", "Designer flooring & paint", "Complete interior finish"],
    popular: true,
  },
  {
    key: "interior",
    name: "Only Interior",
    rateKey: "rate_luxury",
    blurb: "Full interior design & fit-out for an existing space.",
    includes: ["Space planning & 3D design", "Furniture & furnishing", "Kitchen & wardrobes", "Lighting & decor", "Painting & finishes"],
  },
];

function inr(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

export default function CostCalculator({ compact = false }) {
  const s = useSettings();
  const navigate = useNavigate();
  const [area, setArea] = useState(1000);
  const [floors, setFloors] = useState(1);
  const [pkgKey, setPkgKey] = useState("combo");

  const pkg = PACKAGES.find((p) => p.key === pkgKey);
  const rate = Number(s[pkg.rateKey]) || 0;

  const { builtUp, total } = useMemo(() => {
    const a = Math.max(0, Number(area) || 0);
    const f = Math.max(1, Number(floors) || 1);
    const b = a * f;
    return { builtUp: b, total: b * rate };
  }, [area, floors, rate]);

  return (
    <div className="grid gap-0 md:grid-cols-12 overflow-hidden rounded-sm border border-navy/10 bg-white" data-testid="cost-calculator">
      <div className="md:col-span-7 p-8 lg:p-10">
        <div className="flex items-center gap-3">
          <Calculator className="h-6 w-6 text-gold" strokeWidth={1.4} />
          <Label>Estimate your build</Label>
        </div>
        <h3 className="mt-4 font-display text-2xl lg:text-3xl text-navy">Construction cost calculator</h3>

        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Plot area (sq ft)</span>
            <Input type="number" min="0" value={area} onChange={(e) => setArea(e.target.value)} data-testid="calc-area" className="rounded-sm" />
          </label>
          <label className="block">
            <span className="mb-2 block text-xs uppercase tracking-[0.15em] text-navy/50">Number of floors</span>
            <Input type="number" min="1" value={floors} onChange={(e) => setFloors(e.target.value)} data-testid="calc-floors" className="rounded-sm" />
          </label>
        </div>

        <div className="mt-6">
          <span className="mb-3 block text-xs uppercase tracking-[0.15em] text-navy/50">Package</span>
          <div className="grid gap-3 sm:grid-cols-3">
            {PACKAGES.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPkgKey(p.key)}
                data-testid={`calc-pkg-${p.key}`}
                className={`relative rounded-sm border p-4 text-left transition-colors duration-300 ${
                  pkgKey === p.key ? "border-navy bg-navy text-white" : "border-navy/15 bg-white text-navy hover:border-navy"
                }`}
              >
                {p.popular && <span className="absolute -top-2 right-3 rounded-full bg-gold px-2 py-0.5 text-[10px] uppercase tracking-wider text-navy">Popular</span>}
                <div className="font-display text-lg">{p.name}</div>
                <div className={`mt-1 text-xs ${pkgKey === p.key ? "text-white/70" : "text-navy/50"}`}>
                  {inr(Number(s[p.rateKey]) || 0)}/sq ft
                </div>
              </button>
            ))}
          </div>
        </div>

        <ul className="mt-6 grid gap-2 sm:grid-cols-2">
          {pkg.includes.map((inc) => (
            <li key={inc} className="flex items-start gap-2 text-sm text-navy/65">
              <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" /> {inc}
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-5 relative grain overflow-hidden bg-navy text-white p-8 lg:p-10 flex flex-col justify-between">
        <div className="relative z-10">
          <span className="text-xs uppercase tracking-[0.2em] text-gold">Estimated cost</span>
          <div className="mt-3 font-display text-4xl lg:text-5xl" data-testid="calc-total">{inr(total)}</div>
          <p className="mt-2 text-white/50 text-sm">*Indicative estimate. Final cost depends on design, site and material choices.</p>

          <div className="mt-8 space-y-3 text-sm border-t border-white/10 pt-6">
            <Row label="Built-up area" value={`${builtUp.toLocaleString("en-IN")} sq ft`} />
            <Row label="Package" value={pkg.name} />
            <Row label="Rate" value={`${inr(Number(s[pkg.rateKey]) || 0)}/sq ft`} />
          </div>
        </div>

        <button
          onClick={() => navigate("/contact")}
          data-testid="calc-quote"
          className="relative z-10 mt-8 inline-flex items-center justify-center gap-3 rounded-sm bg-gold px-6 py-4 text-navy font-medium hover:bg-white transition-colors duration-300"
        >
          Get a detailed quote <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/50">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
